import React, { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  addCityMasterCity,
  getCountriesForDropdown,
  getCityMasterStates,
  updateCityMasterCity,
} from "../../../api/superAdminApi";

const emptyForm = () => ({
  country: null,
  state: null,
  city: "",
});

/**
 * @param {"single"|"multiple"|null} mode - add mode; ignored when editing
 * @param {string|number|null} defaultCountryId - from page filters when creating
 * @param {string|number|null} defaultStateId - from page filters when creating
 */
export default function AddSingleCityModal({
  open,
  onClose,
  onSuccess,
  initialData = null,
  mode = "single",
  defaultCountryId = null,
  defaultStateId = null,
}) {
  const isEdit = Boolean(initialData?.id);
  const isMultiple = !isEdit && mode === "multiple";
  const [form, setForm] = useState(emptyForm);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hydrating, setHydrating] = useState(false);

  const loadCountries = useCallback(async () => {
    setLoadingCountries(true);
    try {
      const res = await getCountriesForDropdown();
      const list = res.data?.data;
      setCountries(Array.isArray(list) ? list : []);
      return Array.isArray(list) ? list : [];
    } catch {
      setCountries([]);
      toast.error("Failed to load countries.");
      return [];
    } finally {
      setLoadingCountries(false);
    }
  }, []);

  const loadStates = useCallback(async (countryId) => {
    if (!countryId) {
      setStates([]);
      return [];
    }
    setLoadingStates(true);
    try {
      const res = await getCityMasterStates(countryId);
      const list = res.data?.data;
      setStates(Array.isArray(list) ? list : []);
      return Array.isArray(list) ? list : [];
    } catch {
      setStates([]);
      toast.error("Failed to load states.");
      return [];
    } finally {
      setLoadingStates(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    const hydrate = async () => {
      setHydrating(true);
      const countryList = await loadCountries();
      if (initialData?.id) {
        const country =
          countryList.find((c) => String(c.cid) === String(initialData.country)) ||
          (initialData.country
            ? { cid: initialData.country, country_name: initialData.country_name || "" }
            : null);
        const stateList = country?.cid ? await loadStates(country.cid) : [];
        const state =
          stateList.find((s) => String(s.id) === String(initialData.state)) ||
          (initialData.state
            ? { id: initialData.state, state: initialData.state_name || "" }
            : null);
        setForm({
          country,
          state,
          city: initialData.city || "",
        });
      } else {
        // Prefer filters from the City Master page; else fall back to India
        const filterCountryId =
          defaultCountryId && String(defaultCountryId) !== "all" ? defaultCountryId : null;
        const filterStateId =
          defaultStateId && String(defaultStateId) !== "all" ? defaultStateId : null;

        let country =
          (filterCountryId &&
            countryList.find((c) => String(c.cid) === String(filterCountryId))) ||
          null;

        if (!country) {
          country =
            countryList.find((c) => String(c.country_code || "").toUpperCase() === "IN") ||
            countryList.find((c) => String(c.country_name || "").toLowerCase() === "india") ||
            null;
        }

        let state = null;
        if (country?.cid) {
          const stateList = await loadStates(country.cid);
          if (filterStateId) {
            state = stateList.find((s) => String(s.id) === String(filterStateId)) || null;
          }
        } else {
          setStates([]);
        }

        setForm({
          country,
          state,
          city: "",
        });
      }
      setHydrating(false);
    };

    hydrate();
  }, [open, initialData, defaultCountryId, defaultStateId, loadCountries, loadStates]);

  const handleCountryChange = async (value) => {
    setForm({ country: value, state: null, city: form.city });
    await loadStates(value?.cid);
  };

  const canSave = Boolean(form.country?.cid && form.state?.id && form.city.trim());

  const handleClose = () => {
    if (!submitting) onClose();
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSubmitting(true);
    const payload = {
      city: form.city.trim(),
      state_id: form.state.id,
      country_id: form.country.cid,
    };
    try {
      const res = isEdit
        ? await updateCityMasterCity(initialData.id, payload)
        : await addCityMasterCity(payload);
      const body = res.data;
      if (!isEdit && body?.data?.alreadyExists && !body?.data?.multiple) {
        toast.info(body.message || "City already exists");
      } else if (body?.status_code === 200) {
        toast.success(
          body.message ||
            (isEdit
              ? "City updated successfully"
              : isMultiple
                ? "Cities added successfully"
                : "City added successfully")
        );
        onSuccess?.();
        onClose();
      } else {
        toast.error(body?.message || (isEdit ? "Failed to update city." : "Failed to add city."));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || (isEdit ? "Failed to update city." : "Failed to add city."));
    } finally {
      setSubmitting(false);
    }
  };

  const title = isEdit
    ? "Update City"
    : isMultiple
      ? "Add Multiple Cities"
      : "Add Single City";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={isMultiple ? "md" : "sm"} fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
          <Autocomplete
            options={countries}
            loading={loadingCountries || hydrating}
            getOptionLabel={(o) => o.country_name || ""}
            isOptionEqualToValue={(a, b) => String(a?.cid) === String(b?.cid)}
            value={form.country}
            onChange={(_, value) => handleCountryChange(value)}
            renderInput={(params) => (
              <TextField {...params} label="Country" required size="small" placeholder="Select country" />
            )}
          />
          <Autocomplete
            options={states}
            loading={loadingStates || hydrating}
            disabled={!form.country}
            getOptionLabel={(o) => o.state || ""}
            isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
            value={form.state}
            onChange={(_, value) => setForm((prev) => ({ ...prev, state: value }))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="State"
                required
                size="small"
                placeholder={form.country ? "Select state" : "Select country first"}
              />
            )}
          />
          {isMultiple ? (
            <TextField
              label="City names"
              required
              fullWidth
              multiline
              minRows={6}
              value={form.city}
              onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))}
              placeholder="Enter city names separated by commas&#10;e.g. Hyderabad, Warangal, Karimnagar"
              helperText="Separate multiple cities with commas. Sent as one city value; backend splits on commas."
            />
          ) : (
            <TextField
              label="City"
              required
              size="small"
              value={form.city}
              onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))}
              placeholder="Enter city name"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!canSave || submitting || hydrating} variant="contained">
          {submitting ? <CircularProgress size={22} color="inherit" /> : isEdit ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
